import { firstValueFrom, forkJoin, map, zip } from 'rxjs';

import { inject, Injectable } from '@angular/core';
import {
	deleteAllEntities,
	getActiveId,
	getEntity,
	selectActiveEntity,
	selectEntity,
	selectMany,
	setActiveId,
	updateEntities,
	upsertEntities,
} from '@ngneat/elf-entities';
import { updateRequestStatus } from '@ngneat/elf-requests';
import { GUEST_TYPES, GUEST_TYPES_MAP } from '@pages/manifests/constants';
import { Manifest } from '@pages/manifests/interfaces/manifest.interface';
import { ConfigService } from '@shared/services/core/config.service';

import { BaseRepository } from '../base.repository';
import { guestsEntitiesRef, Product, productsEntitiesRef, store, unassignedGuestsEntitiesRef } from './manifests.store';
import { RentalsManifestGuestGuardianMinorStandardClientService } from '@shared/api-client-services/rentals/manifest/guest/guardian-minor/rentals-manifest-guest-guardian-minor-standard.client.service';

const removeDuplicates = (val): any[] => [...new Set(val)];

export type WorkflowType = 'GUIDED_WORKFLOW' | 'EMPLOYEE_WORKFLOW' | 'OPERATOR_WORKFLOW' | 'LAUNCH_WORKFLOW' | 'GUEST_WORKFLOW';
export const WorkflowTypeEnum: { [key: string]: WorkflowType } = {
	GUIDED_WORKFLOW: 'GUIDED_WORKFLOW',
	EMPLOYEE_WORKFLOW: 'EMPLOYEE_WORKFLOW',
	OPERATOR_WORKFLOW: 'OPERATOR_WORKFLOW',
	GUEST_WORKFLOW: 'GUEST_WORKFLOW',
	LAUNCH_WORKFLOW: 'LAUNCH_WORKFLOW',
};

@Injectable({ providedIn: 'root' })
export class ManifestsRepository extends BaseRepository {
	private manifestGuestGuardianMinorStandardClient = inject(RentalsManifestGuestGuardianMinorStandardClientService);

	private _workflowType: WorkflowType;
	set workflowType(type: WorkflowType) {
		this._workflowType = type;
	}

	get workflowType() {
		return this._workflowType;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(private readonly env: ConfigService) {
		super();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - store
	 **-------------------------------------------------------------------------------------
	 */
	get store() {
		return store;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	public isPublicWorkflowType(): boolean {
		return [WorkflowTypeEnum.GUEST_WORKFLOW, WorkflowTypeEnum.OPERATOR_WORKFLOW, WorkflowTypeEnum.GUIDED_WORKFLOW].includes(this.workflowType);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	public isLaunchWorkflowType(): boolean {
		return [WorkflowTypeEnum.LAUNCH_WORKFLOW].includes(this.workflowType);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - isCheckinEnabled
	 **-------------------------------------------------------------------------------------
	 */
	isCheckinEnabled(): boolean {
		return this.getActiveManifest().checkInEnabled;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - isCheckinEnabled
	 **-------------------------------------------------------------------------------------
	 */
	isManifestLocked(): boolean {
		return this.getActiveManifest().locked;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	getManifest = id => store.query(getEntity(id));
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	getActiveManifest = () => {
		const id = this.activeId();
		return store.query(getEntity(id));
	};

	isCheckInEnabled$ = () => {
		const id = this.activeId();
		return store.pipe(selectEntity(id, { pluck: e => e.checkInEnabled }));
	};

	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	activeManifest$ = store.pipe(selectActiveEntity());

	//queres
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	activeId = () => store.query(getActiveId);

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - seed
	 **-------------------------------------------------------------------------------------
	 */
	public async seed(manifest: Manifest, companyId: number, documents = null) {
		return new Promise(async (resolve, reject) => {
			const { manifestProducts, unassignedManifestGuests, ...manifestData } = manifest;

			const assignedGuests = manifestProducts.reduce((acc, curr) => {
				acc.push(curr.manifestGuests.map(guest => ({ ...guest, manifestProductId: curr.id })));
				return acc;
			}, []);

			let guests = [
				...unassignedManifestGuests.map((guest) => ({...guest, manifestProductId: 0 })), 
				...assignedGuests.flat()].map(i => ({
				...i,
			}));

			guests = await Promise.all( guests.map(guest => { 
				if (guest.minor) {
				return {
					...guest,
					minorManifestGuestIds: [],
					guardian: false
				};
			}

				return firstValueFrom(this.manifestGuestGuardianMinorStandardClient
					.getGuardianMinorList(manifest.id, guest.id)
					.pipe(map(guardianMinorList => {
						const minorManifestGuestIds = guardianMinorList
							.filter(e => e.minorManifestGuestId !== guest.id)
							.map(({ minorManifestGuestId }) => minorManifestGuestId);

						return {
							...guest, 
							minorManifestGuestIds,
							guardian: minorManifestGuestIds.length > 0,
						}
					})))
				}
			));

			guests = guests.map(e => {
				e.isDocumentsCompleted = this.isGuestDocumentsComplete(e);
				return {
					...e,
					signature: e?.approvalSignatureUrl,
				};
			});

			try {
				const isPublicWorkflowType = this.isPublicWorkflowType();
				store.update(
					upsertEntities(
						manifestProducts.map(e => {
							const settings = documents.find(p => p.id === e.id);
							const operators = e.manifestGuests.filter(e =>
								[GUEST_TYPES.operator, GUEST_TYPES.primaryOperator].includes(e.manifestGuestTypeId)
							);
							e.manifestGuests = e.manifestGuests.map(record => this.isGuestComplete(record, manifestData, settings.documents));
							return {
								...e,
								productType: e.productTypeName,
								manifestGuests: e.manifestGuests.map(i => i.id),
								isReady: e.manifestGuests.every(e => e.isComplete) && operators.length > 0 && isPublicWorkflowType === false,
							};
						}),
						{ ref: productsEntitiesRef }
					),
					upsertEntities(guests, { ref: guestsEntitiesRef }),
					upsertEntities(unassignedManifestGuests.map((guest) => ({...guest, manifestProductId: 0 })), { ref: unassignedGuestsEntitiesRef }),
					upsertEntities({
						id: manifestData.id,
						reservationId: manifestData.reservationId,
						companyId,
						locked: false,
						finalized: false,
						products: manifestProducts.map(e => e.id),
						guests: guests.map(e => e.id),
						unassignedManifestGuests: unassignedManifestGuests.map(({ id }) => id),
						checkInEnabled: manifestData.checkInEnabled,
						insurancePreselectionCoverageTypeId: manifestData.insurancePreselectionCoverageTypeId,
					})
				);
			} catch (e) {
				console.log(e);
			}
			store.update(setActiveId(manifestData.id));
			store.update(updateRequestStatus('manifests', 'success'));
			resolve(true);
		});
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - isGuestComplete
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	public isGuestComplete(record, manifest, settings) {
		const isPublicWorkflow = this.isPublicWorkflowType();

		const checkinDetails = record?.checkinDetails;
		if (!checkinDetails) return record;
		const missingFields = [];
		const settingsType = GUEST_TYPES_MAP[record.manifestGuestTypeId];

		const signAtHome = settings.signAtHome[settingsType];
		record = {
			...record,
			failedRegistration: false,
			isLicenseRequired: false,
			isLicenseProvided: false,
			isDocumentsRequired: false,
			isDocumentsProvided: false,
		};
		const { documents, VettingQuestions, licensePhoto } = checkinDetails;
		let fieldKeys = Object.keys(checkinDetails).filter(e => !['documents', 'licensePhoto', 'VettingQuestions'].includes(e));
		//check fields
		fieldKeys.forEach(key => {
			const field = checkinDetails[key];
			if (field.required === true && field.provided === false) {
				missingFields.push(key);
			}
		});
		if (missingFields.length === 0) {
			record.isRegistered = true;
		}
		if (missingFields.length > 0) {
			record.isRegistered = false;
		}

		//check Vetting questions
		if (checkinDetails.vettingQuestions.required === true && checkinDetails.vettingQuestions.qualified === false) {
			record.isRegistered = false;
			missingFields.push('vettingQuestions');
			record.failedRegistration = true;
		}
		//check license photo
		if (!isPublicWorkflow) {
			if (licensePhoto.required === true && licensePhoto.provided === false) {
				record.isLicenseRequired = true;
				record.isLicenseProvided = false;
				missingFields.push('licensePhoto');
			}
			if (licensePhoto.required === false) {
				record.isLicenseRequired = false;
			}
			if (licensePhoto.required === true && licensePhoto.provided === true) {
				record.isLicenseRequired = true;
				record.isLicenseProvided = true;
			}
		}

		//check documents
		let documentKeys = Object.keys(documents).filter(e => !['checkinCompleted', 'preCheckinCompleted'].includes(e));
		documentKeys.forEach(key => {
			const field = documents[key];
			if (field.required === true) {
				record.isDocumentsRequired = true;
			}

			if (isPublicWorkflow) {
				if (field.required === true && field.preCheckinSigningRequired === true && field.signed === false) {
					record.isDocumentsProvided = false;
					missingFields.push(key);
				}
				if (field.required === true && field.preCheckinAcceptanceRequired === true && field.accepted === false) {
					record.isDocumentsProvided = false;
					missingFields.push(key);
				}
			}
			if (!isPublicWorkflow) {
				if (field.required === true && field.signed === false) {
					record.isDocumentsProvided = false;
					missingFields.push(key);
				}
			}
		});
		
		//check if all fields are complete
		if (missingFields.length === 0) {
			record.isComplete = true;
		}

		if (!isPublicWorkflow && documents.checkinCompleted) {
			record.isDocumentsRequired = true;
			record.isDocumentsProvided = true;
		}

		if (missingFields.length) {
			const state = record.failedRegistration ? 'ineligible (failed vetting)' : 'more information needed';
			const modal = record.failedRegistration ? 'failure' : 'register';
			record.guestState = {
				color: 'red',
				state,
				modal,
			};
			if (!isPublicWorkflow && record.isRegistered && !record.minor) {
				record.guestState = {
					color: 'blue',
					state: 'ready to check-in',
					modal: 'ready-to-checkin',
					action: missingFields.includes('licensePhoto') && missingFields.length == 1 ? 'license' : 'documents',
				};
			}
			if (!isPublicWorkflow && record.isRegistered && record.minor) {
				record.guestState = {
					color: 'blue',
					state: 'ready to check-in',
					modal: 'minor-ready-to-checkin',
					action: missingFields.includes('licensePhoto') && missingFields.length == 1 ? 'license' : 'documents',
				};
			}
		}
		if (missingFields.length === 0 && isPublicWorkflow) {
			let modal = `operator-checkin${manifest.checkInEnabled ? '-complete' : ''}`;
			if (record.manifestGuestTypeId === GUEST_TYPES.guest) {
				modal = `guest-checkin${manifest.checkInEnabled ? '-complete' : ''}`;
			}
			if (!signAtHome) {
				record.guestCompleted = true;
				record.guestState = {
					color: 'blue',
					state: 'ready to check-in',
					modal,
				};

				return { ...record };
			}
			if (licensePhoto.required === true && licensePhoto.provided === false) {
				record.guestCompleted = true;
				record.guestState = {
					color: 'blue',
					state: 'ready to check-in',
					modal,
				};

				return { ...record };
			}

			record.guestCompleted = true;
			record.guestState = {
				color: 'blue',
				state: 'ready to check-in',
				modal,
			};
		}
		if (missingFields.length === 0 && !isPublicWorkflow) {
			record.guestCompleted = true;
			record.guestState = {
				color: 'green',
				state: 'complete',
				modal: 'complete',
			};
		}

		if (missingFields.length === 0) {
			record.guestCompleted = true;
			record.guestState = {
				color: 'green',
				state: 'complete',
				modal: 'complete',
			};
		}

		if (record.minor && manifest.checkInEnabled && record.guestState.state === 'ready to check-in') {
			record.guestState = {
				color: 'blue',
				state: 'ready to check-in',
				modal: 'minor-ready-to-checkin',
			};
		}

		if (record.minor && !record.accompaniedMinor && manifest.checkInEnabled && record.guestState.modal === 'minor-ready-to-checkin') {
			record.guestState = {
				color: 'orange',
				state: 'pending guardian registration',
				modal: 'pending-guardian-registration',
			};
		}

		if (record.minor &&
			record.guestState.state !== 'complete' &&
			record.guestState.state !== 'ineligible (failed vetting)' &&
			record.guestState.state !== 'ready to check-in'
		) {
			record.guestState = {
				color: 'orange',
				state: 'pending guardian registration',
				modal: 'pending-guardian-registration',
			};
		}

		return { ...record };
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - isGuestDocumentsCompleted
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	public isGuestDocumentsComplete(guest): boolean {
		const { checkinDetails } = guest;
		// when you un-assign a guest, the checkinDetails is null, revisit this with API changes w/ Alex
		if (checkinDetails === null) return false;
		const { documents } = checkinDetails;

		const keys = Object.keys(documents).filter(e => !['checkinCompleted', 'preCheckinCompleted'].includes(e));
		const missingFields = [];
		keys.forEach(key => {
			const field = documents[key];
			const provided = field?.provided || field?.accepted || false;

			if (field.required === true && provided === false) {
				missingFields.push(key);
			}
		});
		return missingFields.length === 0;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	public async validateManifest(): Promise<boolean> {
		const manifest = this.getActiveManifest();
		const products = await firstValueFrom(store.pipe(selectMany(manifest.products, { ref: productsEntitiesRef })));
		return products.every(e => {
			const insuranceComplete = e.insuranceRequired ? e.insurancePurchased || e.insuranceDeclined : true;
			//const insuranceComplete = e.insurancePurchased || e.insuranceDeclined;
			return e?.locked && insuranceComplete && e.manifestGuests.length <= e.maxCapacity;
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - validateProducts
	 **-------------------------------------------------------------------------------------
	 */
	public async validateProducts(): Promise<boolean> {
		const manifest = this.getActiveManifest();
		const products = await firstValueFrom(store.pipe(selectMany(manifest.products, { ref: productsEntitiesRef })));
		return products.every(e => e?.locked);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - log
	 **-------------------------------------------------------------------------------------
	 */
	public log(): void {
		const id = this.activeId();
		const manifest = store.query(getEntity(id));
		const products = store.pipe(selectMany(manifest.products, { ref: productsEntitiesRef }));
		const guests = store.pipe(selectMany(manifest.guests, { ref: guestsEntitiesRef }));
		console.clear();
		firstValueFrom(zip([products, guests])).then(([x, y]) => {
			console.log(' ');
			console.log(' ');
			console.log('%cACTIVE MANIFEST', 'color: blue; font-size: 20px');
			console.table([manifest]);
			console.log(' ');
			console.log(' ');
			console.log('%cACTIVE PRODUCTS', 'color: green; font-size: 20px');
			console.table(x);
			console.log(' ');
			console.log(' ');
			console.log('%cACTIVE GUESTS', 'color: pink; font-size: 20px');
			console.table(y);
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - confirmManifests
	 **-------------------------------------------------------------------------------------
	 */
	public confirmManifests(): void {}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - clear
	 **-------------------------------------------------------------------------------------
	 */
	public clear(): void {
		console.log('clearing manifest');
		store.update(deleteAllEntities({}));
		store.update(deleteAllEntities({ ref: guestsEntitiesRef }));
		store.update(deleteAllEntities({ ref: productsEntitiesRef }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */

	setActiveProduct = id => {
		const manifestId = this.activeId();
		store.update(updateEntities(manifestId, { activeProductId: id }));
	};

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - set
	 **-------------------------------------------------------------------------------------
	 */
	public set({ id, partial }): void {
		store.update(updateEntities(id, entity => ({ ...entity, ...partial })));
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - getManifestLink
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	public getManifestLink({ product = null, workflowType = 'GUIDED_WORKFLOW' }: { product?: Product; workflowType?: string }): string {
		const { companyId, reservationId } = this.getActiveManifest();
		let manifestUrl = (this.env.get('manifestUrl') as string).replace('$reservationId', reservationId.toString());
		if (product) {
			manifestUrl = `${manifestUrl}/guest-form?activeProduct=${product.id}&workflow=${workflowType}`;
			return manifestUrl;
		}
		manifestUrl = `${manifestUrl}/product-list`;
		return manifestUrl;
	}
}
